import theme from '@theme';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Icon, IIconName } from '../../icon';
import AttachmentItems from './AttachmentItems';
import { Attachment } from '@features/siteStatusV2/types';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../app/store';
import { UserRole } from '@features/siteStatusV2/constants';
import { fullNameToInitials, loginToFullName } from '../helpers';
import { useTranslation } from 'react-i18next';
import { ClipText } from '../../clip-text';
import { TranslationNamespaces } from '../../../../../locales/constants/translationNamespaces.enum';

interface HistoryItemProps {
  name: string;
  creationDate: string;
  informations: string;
  attachments?: Attachment[];
}

const HistoryItem: React.FC<HistoryItemProps> = ({
  name,
  creationDate,
  informations,
  attachments
}) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const maxLength = 60;
  const { users, error, isUsersLoading } = useSelector((state: RootState) => state.user);
  const [isTextClip, setIsTextClip] = useState<boolean>(true);
  const { userName, initials } = useMemo(() => {
    const user = users?.find((u) => u.id === name);
    if (user && !error) {
      switch (user.userRole) {
        case UserRole.AXIONE_SUPPORT:
          return {
            userName: t('report.histories.axioneSupport.title'),
            initials: t('report.histories.axioneSupport.initials')
          };
        case UserRole.ROBOT:
          return {
            userName: t('report.histories.robot.title'),
            initials: t('report.histories.robot.initials')
          };
        default:
          return {
            userName: t('report.histories.extern.title'),
            initials: t('report.histories.extern.initials')
          };
      }
    }
    const userName = loginToFullName(name);
    const initials = fullNameToInitials(userName);
    return {
      userName,
      initials
    };
  }, [isUsersLoading]);

  const messageTypeIcon: IIconName =
    attachments && attachments.length > 0
      ? { iconName: 'document-add' }
      : { iconName: 'solid-chat' };

  return (
    <HistoryItemContainer>
      <LeftLine />
      <ProfileImageContainer>
        {isUsersLoading ? <DisplayInitials /> : <DisplayInitials>{initials}</DisplayInitials>}
        <DisplayMessageType>
          <IconMessageType iconName={messageTypeIcon.iconName} />
        </DisplayMessageType>
      </ProfileImageContainer>
      <InformationsContainer>
        <Header>
          {isUsersLoading ? <TitleSkeleton /> : <Title>{userName}</Title>}
          <CreationDate>{creationDate}</CreationDate>
        </Header>
        {attachments && <AttachmentItems attachments={attachments} />}
        <Informations>
          <ClipText text={informations} maxLength={maxLength} isTextClip={isTextClip} />
          {informations.length > maxLength && (
            <ShowMoreButton onClick={() => setIsTextClip((value) => !value)}>
              <ChevronIcon iconName={isTextClip ? 'chevron-up' : 'chevron-down'} />
            </ShowMoreButton>
          )}
        </Informations>
      </InformationsContainer>
    </HistoryItemContainer>
  );
};

export const HistoryItemSkeleton: React.FC = () => {
  return (
    <HistoryItemContainer>
      <LeftLine />
      <ProfileImageContainer>
        <DisplayInitialsSkeleton />
      </ProfileImageContainer>
      <InformationsContainer>
        <Header>
          <TitleSkeleton />
          <CreationDateSkeleton />
        </Header>
        <InformationsSkeleton />
      </InformationsContainer>
    </HistoryItemContainer>
  );
};

const HistoryItemContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
`;

const LeftLine = styled.span`
  position: absolute;
  left: 1rem;
  top: 0.25rem;
  width: 0.1rem;
  height: 100%;
  margin-left: -2px;
  background-color: ${theme.palette.grey[200]};
`;

const ProfileImageContainer = styled.div`
  display: flex;
  position: relative;
  background-color: ${theme.palette.grey[100]};
  border-radius: 100%;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
`;

const DisplayInitials = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.5rem;
  height: 1.5rem;
  background-color: ${theme.palette.grey[50]};
  border-radius: 100%;
  font-size: 10px;
  color: ${theme.palette.grey[500]};
`;

const DisplayInitialsSkeleton = styled(Skeleton)`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 100%;
  background-color: ${({ theme }) => theme.palette.grey[200]};
`;

const DisplayMessageType = styled.div`
  position: absolute;
  top: 1.1rem;
  right: -0.4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width 15px;
  height: 15px;
  border-radius: 100%;
  background-color: ${theme.palette.blue[500]};
`;

const IconMessageType = styled(Icon)`
    width 10px;
    height: 10px;
    color: ${theme.palette.grey[50]};
`;

const InformationsContainer = styled.div`
  width: 100%;
  margin-left: 0.7rem;
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
`;

const Title = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: -0.05px;
  text-align: left;
  color: ${theme.palette.grey[900]};
`;

const TitleSkeleton = styled(Skeleton)`
  width: 6.25rem;
  height: 1rem;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.palette.grey[200]};
`;

const CreationDate = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  text-align: left;
  color: ${theme.palette.grey[400]};
`;

const CreationDateSkeleton = styled(Skeleton)`
  width: 5.75rem;
  height: 1rem;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.palette.grey[200]};
`;

const Informations = styled.div`
  color: ${theme.palette.grey[900]};
  font-size: 0.75rem;
  line-height: 1rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  background-color: ${theme.palette.grey[50]};
  border-radius: 0.375rem;
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
`;

const InformationsSkeleton = styled(Skeleton)`
  background-color: ${({ theme }) => theme.palette.grey[200]};
  border-radius: 0.375rem;
  height: 3rem;
  width: 100%;
`;

const ShowMoreButton = styled.button`
  width: fit-content;
  height: fit-content;
  background-color: ${({ theme }) => theme.palette.grey[50]};
  border-width: 0px;
  margin-left: 0.625rem;
`;

const ChevronIcon = styled(Icon)`
  width: 1rem;
  color: ${({ theme }) => theme.palette.grey[400]};
`;

export default HistoryItem;
